/**
 * The settings-and-superadmin corner of `/v1` — doc 04 §3.1 groups **B** (communities
 * and the classroom), **I** (billing) and **K** (accounts, invites, affiliates,
 * pipelines), plus the one group-C write the Preferences page needs.
 *
 * Six pages are built on this file: Preferences, Indexed Content, Invites & accounts,
 * Affiliates roster, My affiliate page and Pipeline health. What they had in common
 * before v1 was the shape doc 02 W9 names — *write, then re-read the whole list* — and
 * doc 02 W8/W10/W11's three sequential chains. Every mutation below returns what it
 * changed, so none of those pages re-reads anything after a write.
 *
 * WHY THESE THREE GROUPS SHARE A FILE. The convention `@api/v1/envelope` sets is one
 * module per doc-04 §3.1 resource group, and on the read side that is what B, I and K
 * are. They are together here because they are one *owner*: these are the routes no
 * community-scoped page touches, they are consumed by exactly the six pages above, and
 * splitting them into four files of eighty lines each would name four modules after
 * routes rather than after anything a page asks for. `members`, `moderation`,
 * `content`, `insights`, `winbacks` and `questions` are unaffected — this file imports
 * from two of them and duplicates none of them.
 *
 * THE ONE BORROWED ROUTE. `setMemberRole` is group C, whose module is
 * `@api/v1/members`. It lives here because Preferences is the only caller and the
 * members module is another page's; the *types* come from there rather than being
 * restated. Same for `fetchStaff`/`searchMembers`, which are `…/members` with a
 * `?fields=` selection `MemberQuery` does not expose yet — see their notes.
 *
 * NOT HERE: `GET /v1/me/affiliate`, which is group A and lives in `@api/v1/me`
 * beside the bootstrap — it is account-scoped, like everything else on `/v1/me`. Read
 * `fetchMyAffiliate`'s note before wiring it: the route still ships without
 * `referrals[]`, `payouts[]` and `paying_community_count`, which are three of the five
 * blocks `AdminAffiliate` renders, so that page cannot swap onto it yet.
 */

import { localDayStartISO } from './me';
import type { AccountPlanStatus, Bootstrap, MeCommunity } from './me';

import { apiDelete, apiGet, apiPatch, apiPost } from './http';
import { community } from './envelope';
import type { CommunityRef, ListEnvelope, QueryParams } from './envelope';

import type { MemberResource } from './members';


// ---------------------------------------------------------------------------
// Walking a cursor-paginated list to the end
// ---------------------------------------------------------------------------

/**
 * Every list in this file is cursor-paginated in v1 and was an unbounded bare JSON
 * array before it (`01-backend-surface.md` §4.5 counts them among the ten endpoints
 * that grow without bound). The four superadmin screens render whole tables with no
 * pager, so they walk the cursor rather than growing a paging UI in a migration PR.
 *
 * The FIRST page's object is returned with its `items` replaced by the concatenation,
 * so a route's sibling blocks — `/v1/pipelines`' `global_steps`, `stages`, `jobs` and
 * the two fleet-wide counts — survive the walk instead of being dropped on the floor.
 *
 * `maxPages` is a stop, not a policy: at the 200-row maximum page size it is 2,000
 * rows, well past any of these tables, and it means a server that kept handing back a
 * cursor could not spin the browser forever.
 */
async function collect<T, P extends ListEnvelope<T>>(
  load: (cursor: string | null) => Promise<P>,
  maxPages = 10,
): Promise<P> {
  const first = await load(null);
  if (first.next_cursor == null) return first;
  const items = [...first.items];
  let cursor: string | null = first.next_cursor;
  for (let n = 1; cursor != null && n < maxPages; n += 1) {
    const next: P = await load(cursor);
    items.push(...next.items);
    cursor = next.next_cursor;
  }
  first.items = items;
  first.next_cursor = cursor;
  return first;
}

/** The largest page the server will serve (`api/v1/common.py:MAX_LIMIT`). Asking for
 *  it is what keeps `collect` at one request for every table these pages render. */
const MAX_LIMIT = 200;

// ===========================================================================
// Group B — communities
// ===========================================================================

export interface CommunityPage extends ListEnvelope<MeCommunity> {}

/**
 * `GET /v1/communities` — the fleet for a superadmin, the held set for a creator,
 * searchable server-side.
 *
 * THIS IS NOT `community.list`. The bootstrap's `communities` is the caller's HELD
 * set even for a superadmin (`api/v1/principal.py`), which is right for the sidebar
 * switcher and wrong for the manage-communities dialog on the accounts page — that
 * dialog grants access to any community on the platform. Before v1 the dialog read
 * `community.list`, which for a superadmin was the whole fleet downloaded by
 * `GET /admin/communities` on every page load (`02-admin-consumption.md` §4.8). This
 * route is what the dialog asks instead: `?q=`, on demand, one page.
 */
export function fetchCommunities(
  opts: { q?: string; limit?: number; cursor?: string | null } = {},
): Promise<CommunityPage> {
  return apiGet<CommunityPage>('/communities', {
    q: opts.q?.trim() || undefined,
    limit: opts.limit,
    cursor: opts.cursor ?? undefined,
  });
}

// --- the classroom (Indexed Content) ---------------------------------------

/** The four node kinds, as the PATCH body's discriminator spells them (§1.6: `kind`,
 *  singular, everywhere — the legacy body called it `node_type`). */
export type ClassroomNodeKind = 'course' | 'section' | 'page' | 'resource';

export interface ClassroomResource {
  id: number;
  title: string | null;
  kind: string;
  position: number | null;
  enabled: boolean;
  link: string | null;
  /** null until fetched; `ok | unavailable | unsupported | failed`. */
  content_status: string | null;
}

export interface ClassroomPage {
  id: number;
  title: string | null;
  external_id: string | null;
  position: number | null;
  enabled: boolean;
  locked: boolean;
  resources: ClassroomResource[];
}

export interface ClassroomSection {
  id: number;
  name: string | null;
  position: number | null;
  enabled: boolean;
  pages: ClassroomPage[];
}

export interface ClassroomCourse {
  id: number;
  title: string | null;
  slug: string | null;
  position: number | null;
  enabled: boolean;
  locked: boolean;
  sections: ClassroomSection[];
}

/** The five header integers, counted server-side over exactly the population the
 *  client counted — `AdminContent.svelte` recomputed them with a triple-nested loop on
 *  every render, and re-read the whole tree to recompute them after a bulk toggle
 *  (W8). Resources are toggleable but are not in `off`; only courses and pages carry
 *  `locked`. */
export interface ClassroomTotals {
  courses: number;
  sections: number;
  pages: number;
  off: number;
  locked: number;
}

export interface Classroom {
  community: CommunityRef;
  courses: ClassroomCourse[];
  totals: ClassroomTotals;
}

/** What a `PATCH` answers with: the new totals and how many nodes actually moved —
 *  deliberately NOT the tree, which the client already holds. That omission IS the
 *  W8 collapse. `affected` counts nodes whose `enabled` changed, so a no-op toggle
 *  reports 0 rather than claiming work. */
export interface ClassroomPatched {
  community: CommunityRef;
  affected: number;
  totals: ClassroomTotals;
}

/** `GET /v1/communities/{skool_id}/classroom` — Course → Section → Page → Resource,
 *  with the header totals precomputed. Unpaginated by declaration (§1.3): a tree,
 *  consumed whole. */
export function fetchClassroom(skoolId: number): Promise<Classroom> {
  return apiGet<Classroom>(community(skoolId, '/classroom'));
}

/** Flip specific nodes. One declarative `PATCH` replacing `POST …/toggle`; the body
 *  is the full desired state of each named node, so it is idempotent. */
export function patchClassroomNodes(
  skoolId: number,
  nodes: { kind: ClassroomNodeKind; id: number; enabled: boolean }[],
): Promise<ClassroomPatched> {
  return apiPatch<ClassroomPatched>(community(skoolId, '/classroom'), { nodes });
}

/** Flip everything. `{all: true, enabled}` is the same mutation over a different
 *  subject, which is why it is this route and not a second one — and the response's
 *  `totals` is why the page no longer re-reads the tree afterwards (W8). */
export function patchClassroomAll(
  skoolId: number,
  enabled: boolean,
): Promise<ClassroomPatched> {
  return apiPatch<ClassroomPatched>(community(skoolId, '/classroom'), { all: true, enabled });
}

// ===========================================================================
// Group C (borrowed) — staff, member search and the role write
// ===========================================================================

/**
 * A member as the Preferences page needs them: identity, their community role, and —
 * when asked for — the one contribution number the search results render.
 *
 * This is a `?fields=` selection of the same `…/members` route `@api/v1/members`
 * owns. It is declared here rather than there because `MemberQuery` has no `fields`
 * option and adding one is that module's call, not this one's: `role` is NOT in the
 * list route's default field set (`api/v1/members.py:_list_select`, default
 * `{membership, stats}`), and the staff chips render nothing but the role.
 */
export interface StaffMember {
  member_id: number;
  external_id: string;
  /** Genuinely nullable on the wire — `identity_of` can hold no handle. */
  handle: string | null;
  name: string;
  picture_profile: string | null;
  role: string | null;
  /** Present only on the search call, which asks for the `stats` block. */
  stats?: { activity: number | null } | null;
}

export interface StaffPage extends ListEnvelope<StaffMember> {
  community: CommunityRef;
}

/** `?staff=only` — what `GET /admin/moderators` was, which was `GET /admin/members`
 *  with one clause swapped (`01-backend-surface.md` §4.9). A moderation team is
 *  bounded by design, so one 200-row page is the whole list. */
export function fetchStaff(skoolId: number): Promise<StaffPage> {
  return apiGet<StaffPage>(community(skoolId, '/members'), {
    staff: 'only',
    fields: ['role'],
    limit: MAX_LIMIT,
  });
}

/** The "add a team member" typeahead. `staff=exclude` is the default and is what
 *  `GET /admin/members` did, so the search keeps returning the people who are not on
 *  the team yet. `stats` is asked for because the row shows an activity count. */
export function searchMembers(
  skoolId: number,
  q: string,
  limit = 20,
): Promise<StaffPage> {
  return apiGet<StaffPage>(community(skoolId, '/members'), {
    q: q.trim(),
    staff: 'exclude',
    fields: ['role', 'stats'],
    limit,
  });
}

/**
 * `PATCH /v1/communities/{skool_id}/members/{member_id}` — assign a community role,
 * or reset someone to `member`.
 *
 * Replaces `POST /admin/members/set-role`, whose response was `{status: "ok"}` and
 * whose caller therefore had to reconstruct the changed row from whatever the click
 * came from. This returns the member in the representation the GET uses, so the staff
 * list is patched from the server's answer rather than from the client's guess.
 */
export function setMemberRole(
  skoolId: number,
  memberId: number,
  role: 'owner' | 'admin' | 'moderator' | 'member',
): Promise<MemberResource> {
  return apiPatch<MemberResource>(community(skoolId, `/members/${memberId}`), { role });
}

/**
 * `PATCH /v1/communities/{skool_id}/members/{member_id}/contact` — the owner's own note
 * of how to reach someone.
 *
 * Skool tells a moderator no member's email (`metadata.mbme` is admin/owner-only), so
 * this is the only place one can live. Scoped to ONE community: two owners holding the
 * same Skool user keep separate values and neither can read the other's.
 *
 * A SUB-RESOURCE rather than another field on the member PATCH, because that route
 * assigns a role — a different authority from storing personal details.
 *
 * OMIT a key to leave it alone; send `null` to clear it. Passing `undefined` for one of
 * the two is how "save just the phone" avoids wiping the email.
 */
export function setMemberContact(
  skoolId: number,
  memberId: number,
  patch: { email?: string | null; phone?: string | null },
): Promise<MemberResource> {
  return apiPatch<MemberResource>(
    community(skoolId, `/members/${memberId}/contact`), patch);
}

// --- voices, with the full profile ------------------------------------------

/** The four per-approach facets, each null where there were not enough examples of
 *  that mode; the whole object is null for a staff member with no generated voice. */
export interface VoiceFacets {
  acknowledge: string | null;
  celebrate: string | null;
  help: string | null;
  correct: string | null;
}

export interface StaffVoice {
  member_id: number;
  handle: string | null;
  name: string | null;
  role: string | null;
  picture_profile: string | null;
  is_default: boolean;
  profile_text: string | null;
  profiles: VoiceFacets | null;
  source_count: number | null;
  updated_at: string | null;
}

export interface StaffVoicePage extends ListEnvelope<StaffVoice> {
  community: CommunityRef;
}

/**
 * `GET /v1/communities/{skool_id}/voices?include=profile` — the Preferences reading,
 * where `@api/v1/moderation`'s `fetchVoices` is the composer's picker reading of
 * the same route (`?with_profile_only=true`, no profile block). One route, two
 * selections; the legacy surface had two routes for it (`/admin/voices` and
 * `/admin/moderation/voices`).
 *
 * `with_profile_only` is explicitly false: this page lists the WHOLE team and says
 * "no voice yet" against the ones without one, which is the state the page exists to
 * make visible.
 */
export function fetchStaffVoices(skoolId: number): Promise<StaffVoicePage> {
  return apiGet<StaffVoicePage>(community(skoolId, '/voices'), {
    with_profile_only: false,
    include: ['profile'],
    limit: MAX_LIMIT,
  });
}

// ===========================================================================
// Group I — billing
// ===========================================================================

/** Where to send the browser, plus the community it is paying for and that
 *  community's plan state as of now — so the paywall that triggered the redirect can
 *  re-render without a second request. */
export interface CheckoutSession {
  url: string;
  community: CommunityRef;
  plan_status: string;
}

/**
 * `POST /v1/communities/{skool_id}/billing/checkout`.
 *
 * Community-scoped in the PATH, where the legacy route took `skool_id` in the body —
 * so the scope check is the one every other community route uses. The gate is `admin`
 * and not `active` on purpose: the whole point is to pay past the paywall.
 *
 * NOTE FOR THE TRIAL/PAYWALL PAGES. `startCheckout` in `lib/admin.ts` is still live
 * for `AdminTrial`, `TrialBanner` and `ExpiredPaywall`. When those migrate they should
 * call THIS rather than write a second wrapper — the URL is the only thing all four
 * callers read, so `(await startCheckout(id)).url` is a drop-in.
 */
export function startCheckout(skoolId: number): Promise<CheckoutSession> {
  return apiPost<CheckoutSession>(community(skoolId, '/billing/checkout'));
}

/** `POST /v1/me/billing/portal` — account-scoped, because there is one Stripe
 *  Customer per user however many communities they bill for. 409 `no_billing_account`
 *  when they have never checked out; that is a state, not a failure. */
export function openBillingPortal(): Promise<{ url: string }> {
  return apiPost<{ url: string }>('/me/billing/portal');
}

/**
 * `POST /v1/me/billing/sync` — reconcile this owner's Stripe subscriptions into our
 * cache, and answer with **the `/v1/me` body** (doc 04 §4.7, W10).
 *
 * The chain it replaces is five sequential round trips: `POST /admin/billing/sync` →
 * `GET /admin/me` → `GET /admin/communities` → `GET /admin/communities` *again,
 * forced* → the page's own two fetches. The response here is the same `Bootstrap` the
 * bootstrap returns, built by the same `api.v1.common.me_body`, so a creator's plan
 * state cannot depend on which route answered.
 *
 * `skoolId` selects which community's badges to compute, exactly as on `GET /v1/me`;
 * pass the optimistic selection. Omitted, `badges` is absent.
 *
 * Typed as `Bootstrap` rather than re-declared: `@api/v1/me` owns that shape and two
 * declarations of one body drift.
 */
export function syncBilling(skoolId: number | null = null): Promise<Bootstrap> {
  return apiPost<Bootstrap>('/me/billing/sync', {}, {
    since: localDayStartISO(),
    ...(skoolId != null ? { skool_id: skoolId } : {}),
  });
}

// ===========================================================================
// Group K — staff and superadmin
// ===========================================================================

// --- accounts ---------------------------------------------------------------

/** The affiliate side of an account, or `null` when it was never enrolled.
 *  `rate_bps` is the EFFECTIVE rate and `disabled` is the kill switch that keeps
 *  the code — same meanings as on `Affiliate`, which is the console ROW. */
export interface AccountAffiliate {
  code: string;
  rate_bps: number;
  has_custom_rate: boolean;
  disabled: boolean;
  referred_count: number;
  totals: AffiliateTotals;
}

/** Who referred this account (`?via=`, stamped write-once at registration).
 *  `code` may be null — an affiliate can be un-enrolled after referring somebody,
 *  and the attribution still stands. */
export interface ReferrerRef {
  user_id: number;
  name: string;
  code: string | null;
}

/**
 * One account, as both `GET /v1/accounts` and `PATCH /v1/accounts/{id}` return it.
 *
 * `communities` is the same `Community` representation `/v1/me` returns (built by the
 * one shared assembly server-side), which is what makes the per-community
 * `plan_status` here the SAME answer the owner sees on their own panel. Billing is per
 * community, so that — not the account-level `plan_status` beside it — is where an
 * account's money state actually lives. Account-level `plan_status: 'free_for_life'`
 * means the manual comp lever (`paid_at`) is on; `'paid'` means a real subscription.
 *
 * EVERY FIELD BUT `user_id` IS OPTIONAL, and that is the field-selection contract
 * rather than a hedge: `user_id` is the route's identity field and is always emitted,
 * and everything else is in the selectable set, so `?fields=name&fields=email` really
 * does come back as three keys. The type says what the wire says.
 *
 * `role`, `created_at`, `last_login_at`, `affiliate` and `referred_by` are OUTSIDE the
 * server's default set — each costs a query the invite/affiliate dialogs have no use
 * for. Ask for them with `fields: ['*']`, which is what the accounts console does.
 */
export interface Account {
  user_id: number;
  email?: string;
  name?: string;
  role?: 'creator' | 'superadmin';
  communities?: MeCommunity[];
  pending_skool_url?: string | null;
  plan_status?: AccountPlanStatus;
  trial_ends_at?: string | null;
  paid_at?: string | null;
  created_at?: string | null;
  /** `null` = never signed in since the column landed. A real state, not a gap. */
  last_login_at?: string | null;
  affiliate?: AccountAffiliate | null;
  referred_by?: ReferrerRef | null;
}

export interface AccountPage extends ListEnvelope<Account> {}

/**
 * `{plan?, skool_ids?}` — the two legacy verb routes, declaratively.
 *
 * Both fields are resolved through `model_fields_set` server-side, so **an omitted
 * field is left alone and an explicit `null` means something**: `plan: null` clears
 * the manual paid stamp, which is what `set-plan` with `paid: false` did. Build this
 * object conditionally — `{plan: undefined}` would serialise the key away, which is
 * the same as omitting it, but `exactOptionalPropertyTypes` will not let you write it
 * anyway.
 */
export interface AccountPatch {
  /** `'free_for_life'` stamps the manual comp; `null` clears it. `'paid'` is the
   *  pre-#156 spelling — still accepted by the server for one release so an already-open
   *  console keeps working, but nothing here should send it. */
  plan?: 'free_for_life' | 'paid' | null;
  /** The full desired set, not a delta — idempotent, so a checkbox UI posts its
   *  state directly. An empty list revokes everything. */
  skool_ids?: number[];
}

/** Every creator account, newest first. `?q=` is server-side, which is the point:
 *  the accounts table, the invite dialog and the affiliate-enrollment dialog each
 *  downloaded EVERY account on the platform and filtered it in the browser
 *  (`02-admin-consumption.md` §5.4). `fields` trims the row to what a picker renders. */
export function fetchAccounts(
  opts: {
    q?: string;
    role?: AccountRole;
    limit?: number;
    cursor?: string | null;
    fields?: string[];
  } = {},
): Promise<AccountPage> {
  const params: QueryParams = {
    q: opts.q?.trim() || undefined,
    role: opts.role,
    limit: opts.limit,
    cursor: opts.cursor ?? undefined,
    fields: opts.fields?.length ? opts.fields : undefined,
  };
  return apiGet<AccountPage>('/accounts', params);
}

/** Which accounts the collection means. The server defaults to `creator`, which is
 *  what the pickers want; the accounts console asks for `all`, since "every user on
 *  the platform" has to include the staff who administer it. */
export type AccountRole = 'creator' | 'superadmin' | 'all';

/** The whole accounts table, walked. Defaults to creators with the default field set
 *  — pass `{ role: 'all', fields: ['*'] }` for the console's full rows. */
export function fetchAllAccounts(
  opts: { role?: AccountRole; fields?: string[] } = {},
): Promise<AccountPage> {
  return collect((cursor) => fetchAccounts({ ...opts, limit: MAX_LIMIT, cursor }));
}

/** Set the plan stamp and/or exactly which communities an account administers, and
 *  get the account back — the two W9 chains on the accounts page (`POST … ; await
 *  reloadTheWholeList()`) become this one call. */
export function patchAccount(userId: number, patch: AccountPatch): Promise<Account> {
  return apiPatch<Account>(`/accounts/${userId}`, patch);
}

// --- invites ----------------------------------------------------------------

export interface Invite {
  id: number;
  token: string;
  skool_url: string;
  community_name: string | null;
  note: string | null;
  status: 'pending' | 'used' | 'expired';
  expires_at: string | null;
  used_at: string | null;
  used_by_email: string | null;
  created_at: string | null;
}

export interface InvitePage extends ListEnvelope<Invite> {}

export function fetchInvites(
  opts: { limit?: number; cursor?: string | null } = {},
): Promise<InvitePage> {
  return apiGet<InvitePage>('/invites', {
    limit: opts.limit,
    cursor: opts.cursor ?? undefined,
  });
}

export function fetchAllInvites(): Promise<InvitePage> {
  return collect((cursor) => fetchInvites({ limit: MAX_LIMIT, cursor }));
}

/** Mint a pre-bound registration link and return it — the page builds the register
 *  URL from `token` and prepends the row instead of re-reading the table (W9).
 *  A URL that is not a Skool community URL is a 422, not an invite nobody can use. */
export function createInvite(input: { skoolUrl: string; note?: string }): Promise<Invite> {
  return apiPost<Invite>('/invites', {
    skool_url: input.skoolUrl,
    ...(input.note ? { note: input.note } : {}),
  });
}

// --- account invites (the magic links the accounts console issues) ----------
//
// A different object from `Invite` above. That one pre-binds a REGISTRATION to a
// community URL we may not have ingested; this one hands a named person access to
// communities we already hold, and is claimed by clicking rather than by signing up.

/** One issued magic link. `token` and not a URL, matching `Invite`: the API has no
 *  stored frontend origin, so the console assembles `${location.origin}/invite?token=`
 *  itself — the same way it already builds registration links. */
export interface AccountInvite {
  id: number;
  email: string;
  token: string;
  communities: CommunityRef[];
  status: 'pending' | 'used' | 'revoked' | 'expired';
  expires_at: string | null;
  used_at: string | null;
  created_at: string | null;
}

export interface AccountInvitePage extends ListEnvelope<AccountInvite> {}

/** Issued links, newest first. `status: 'pending'` is what the console shows — a link
 *  nobody has clicked yet is a thing to chase; a used one is history. */
export function fetchAccountInvites(
  opts: { status?: AccountInvite['status']; limit?: number; cursor?: string | null } = {},
): Promise<AccountInvitePage> {
  return apiGet<AccountInvitePage>('/account-invites', {
    status: opts.status,
    limit: opts.limit,
    cursor: opts.cursor ?? undefined,
  });
}

export function fetchAllAccountInvites(
  opts: { status?: AccountInvite['status'] } = {},
): Promise<AccountInvitePage> {
  return collect((cursor) => fetchAccountInvites({ ...opts, limit: MAX_LIMIT, cursor }));
}

/** Mint a link for one email and one or more communities. The email need NOT be an
 *  existing account — an unknown address creates the account when the link is clicked.
 *  422 on a malformed email or an unknown community id, both checked while the dialog
 *  is still open rather than a week later at claim time. */
export function createAccountInvite(
  input: { email: string; skoolIds: number[] },
): Promise<AccountInvite> {
  return apiPost<AccountInvite>('/account-invites', {
    email: input.email,
    skool_ids: input.skoolIds,
  });
}

/** Kill an unclaimed link and get it back in its new state (W9) — the only fix for a
 *  link sent to the wrong address, since the token is a bearer credential for that
 *  email until it is used or expires. 409 `invite_already_used` on a claimed one:
 *  the communities are granted, so take those back with `patchAccount` instead. */
export function revokeAccountInvite(inviteId: number): Promise<AccountInvite> {
  return apiDelete<AccountInvite>(`/account-invites/${inviteId}`);
}

// --- affiliates (the superadmin console) ------------------------------------

/** The three balance buckets plus their sum. Nested, where the legacy row had them
 *  flat — one `AffiliateTotals` is emitted by four routes (§2 shared shapes). */
export interface AffiliateTotals {
  pending_cents: number;
  payable_cents: number;
  paid_cents: number;
  lifetime_cents: number;
}

/** One console row — what all four affiliate routes return. `rate_bps` is the
 *  EFFECTIVE rate (the account's override, else the program default) and
 *  `has_custom_rate` says which of the two it came from, so the console can show
 *  "default" without knowing the default. */
export interface Affiliate {
  user_id: number;
  name: string;
  email: string;
  code: string | null;
  rate_bps: number;
  has_custom_rate: boolean;
  disabled: boolean;
  referred_count: number;
  totals: AffiliateTotals;
}

export interface AffiliatePage extends ListEnvelope<Affiliate> {}

export function fetchAffiliates(
  opts: { limit?: number; cursor?: string | null } = {},
): Promise<AffiliatePage> {
  return apiGet<AffiliatePage>('/affiliates', {
    limit: opts.limit,
    cursor: opts.cursor ?? undefined,
  });
}

export function fetchAllAffiliates(): Promise<AffiliatePage> {
  return collect((cursor) => fetchAffiliates({ limit: MAX_LIMIT, cursor }));
}

/** Enroll an account. `code` defaults server-side to a deduped kebab slug of the
 *  name; `rateBps` omitted leaves them on the program default. 409 `already_an_affiliate`
 *  / 409 `code_taken`, 422 on a malformed code or an out-of-range rate. */
export function createAffiliate(
  userId: number,
  opts: { code?: string; rateBps?: number } = {},
): Promise<Affiliate> {
  return apiPost<Affiliate>('/affiliates', {
    user_id: userId,
    ...(opts.code ? { code: opts.code } : {}),
    ...(opts.rateBps != null ? { rate_bps: opts.rateBps } : {}),
  });
}

/** `rate_bps` is resolved through `model_fields_set`: omitted leaves the override
 *  alone, an explicit `null` clears it back to the program default, an int sets it.
 *  `disabled` is the kill switch — the code is kept either way, so existing links keep
 *  resolving to a real account and simply stop earning. */
export function patchAffiliate(
  userId: number,
  patch: { rate_bps?: number | null; disabled?: boolean },
): Promise<Affiliate> {
  return apiPatch<Affiliate>(`/affiliates/${userId}`, patch);
}

/** A payout, as created. `amount_cents` is the net actually sent (clawbacks already
 *  netted), `rows` is how many commission rows it covered, and `affiliate` is the
 *  UPDATED console row — which is what lets the page patch locally instead of
 *  re-reading the whole table (W9). */
export interface Payout {
  amount_cents: number;
  rows: number;
  paid_out_at: string;
  affiliate: Affiliate;
}

/** `POST /v1/affiliates/{user_id}/payouts` — a payout is a thing that is created,
 *  where the legacy spelling was `POST …/mark-paid`. 409 `nothing_payable` when
 *  nothing qualified or the net is <= 0; the rows stay payable for a later run. */
export function createPayout(userId: number): Promise<Payout> {
  return apiPost<Payout>(`/affiliates/${userId}/payouts`);
}

// --- pipelines (the health grid) --------------------------------------------

/** How far a RUNNING step has got, from the `cli_progress.Progress` the sub-job builds.
 *  Absent on anything not running: a finished step's counts are the real answer, and a
 *  stale done/total on a closed row would read as if it had stopped short. `total` is
 *  null for a step that reported progress before it knew its denominator — better than
 *  a percentage of a guess. `unit` names what is being counted ("pages", "moderators",
 *  "rungs"). */
export interface StepProgress {
  done: number;
  total: number | null;
  unit: string | null;
  pct: number | null;
}

/** One (community × step) cell: the LATEST run of that step, from the job ledger. */
export interface StageCell {
  status: string;
  ran_at: string | null;
  error: string | null;
  detail: Record<string, unknown> | null;
  duration_ms: number | null;
  mode: string | null;
  /** What ran this step: `flow` (the schedule), `cli` (a hand-run), `helper` (the
   *  extension). The grid shows the LATEST run of each step whoever ran it, so without
   *  this a manual `--mode redo` is indistinguishable from the tick — and it takes the
   *  cell that is meant to answer "is the schedule healthy". */
  trigger?: string | null;
  progress?: StepProgress | null;
  /** FETCH-PLANE CELLS ONLY: when the scheduler will next hand this walk out. A ledger
   *  cell never sets it and, under `response_model_exclude_unset`, never carries it —
   *  the job ledger records what HAS run and has no equivalent fact, because "what is
   *  next" is recomputed inside each job there. */
  next_due_at?: string | null;
}

/** A demand/reconcile kind's BACKLOG, fleet-wide. Not a cell, on purpose:
 *  `post_comments` is one row per post whose comments moved — 92k on the largest
 *  community — so the answerable question is "how much is outstanding and how old is the
 *  front of the queue", never "show me the rows". */
export interface DemandSummary {
  open: number;
  backed_off: number;
  oldest_due_at?: string | null;
}

/** The walk registry's own health, beside the grid it also feeds.
 *
 *  `enabled` is the server's `scheduler_enabled` — the switch that decides whether
 *  anything claims these walks at all. Without it a registry full of overdue walks and a
 *  registry nothing is driving look identical.
 *
 *  `demand` and `fleet` are the two parts that cannot be a column of a per-community
 *  grid: the demand rows because there are tens of thousands of them, the fleet walks
 *  (`self_profile`, `groups_list`) because they belong to a scraping identity rather than
 *  to any community. `fleet` is keyed `"<kind>:<egress id>"`. */
export interface FetchPlane {
  enabled: boolean;
  demand: Record<string, DemandSummary>;
  fleet: Record<string, StageCell>;
}

/** One step the pipeline is expected to perform — a SUB-JOB of one of `jobs.JOBS`, in
 *  the order a flow runs them. This is what lets the grid render the WHOLE expected set
 *  as fixed columns, so a step the ledger has no row for yet shows as `never` instead of
 *  vanishing.
 *
 *  The order is by HOW OFTEN the step runs — fastest first, no-timer steps last. It was
 *  the execution order until the cut-over, which was right while one flow performed every
 *  step in sequence; the fetch plane has no execution order at all, since a walk is
 *  claimed when it comes due. Sorted by rate, the left of the grid is what should have
 *  moved in the last few minutes and the right is what may be hours old. The sort is
 *  stable, so steps sharing a rate keep the order their flow declared them in.
 *
 *  `flow` is what the grid bands by, because a flow is what actually runs; null means no
 *  timer fires it. `job` is the unit you re-run (`pipeline.py job <job> <community>`).
 *  `scope` says whose row it files under — `person` is `skool-user-profile-page`, which
 *  takes a handle and so can never have a cell in a per-community grid.
 *  `cadence` is what "overdue" is judged against: `minute` is the automations flow,
 *  `fast` every sync pass, `daily` self-throttled to ~24h (the classroom crawl, the plan
 *  ladder), `manual` no timer and so never overdue. */
export interface StageMeta {
  name: string;
  job: string;
  /** What this step does, in a line — `SubJob.description` verbatim, the same string
   *  `pipeline.py jobs` prints, so the page and the CLI cannot drift. */
  description: string;
  job_description: string;
  flow: string | null;
  scope: 'community' | 'fleet' | 'person';
  cadence: 'minute' | 'fast' | 'daily' | 'manual';
  /** FETCH-PLANE COLUMNS ONLY: the walk's declared interval, seconds. `cadence` cannot
   *  carry it — 5 min and 15 min both collapse to `fast`, an hour and a day both to
   *  `daily` — so a 2-minute join probe and a 15-minute feed sweep would head up
   *  identically. Absent on every ledger step. */
  interval_secs?: number | null;
}

export interface PipelineCommunity {
  id: number;
  name: string;
  slug: string;
  logo_url: string | null;
  /** The scraper account has lost membership — every stage fails by definition. */
  no_access: boolean;
  /** A real failure: excludes `no_access`, which is an access problem rather than
   *  pipeline breakage. Computed server-side; the page scanned every step of every
   *  community on every render to derive it (`02-admin-consumption.md` §5.1). */
  failed: boolean;
  steps: Record<string, StageCell>;
}

/** How far a RUNNING job has got. Every job pre-creates one pending row per sub-job
 *  before it starts, so the denominator is always real. */
export interface JobProgress {
  done: number;
  total: number | null;
  /** What is being counted: `steps` for a job's sub-jobs, `communities` for a flow that
   *  walks the fleet. A flow with fleet-wide chores counts those too, so it stays
   *  `steps` rather than claiming a community count it does not have. */
  unit?: string;
}

/** One row of the recent-jobs history panel — an onboard/refresh/redo run, straight
 *  from `db.job_ledger.recent_jobs`. `slug` is null for a fleet-wide run. */
export interface JobRecord {
  id: number;
  type: string;
  skool_id: number | null;
  slug: string | null;
  trigger: string;
  status: string;
  summary: string | null;
  started_at: string | null;
  finished_at: string | null;
  /** `flow` for a whole pass, `job` for one job inside it. Both are open rows while a
   *  flow runs, and the names alone do not say which contains which. */
  kind?: 'flow' | 'job';
  /** Present only while `status === 'running'`. */
  progress?: JobProgress | null;
  /** The step in flight and how far into it, while `status === 'running'`. Distinct
   *  from `progress`, which counts whole steps: a long sub-job leaves that at 0 for
   *  minutes while this one moves, so the two together answer "how much is done" and
   *  "is anything happening". `stage` is always set; the progress fields are absent
   *  until the step reports any. */
  step?: (Partial<StepProgress> & { stage: string }) | null;
}

/** A community that is not ingested yet, and what the scheduler intends to do about
 *  it. Derived from the ledger, so a community joined but never yet attempted is
 *  absent until its first attempt (the deep tier makes one within ~5 min). */
/** A community waiting to be ingested, straight from `onboard_queue`. It used to be
 *  inferred from ledger job history — the only way to answer this before the queue was
 *  a table, and an answer that stopped arriving when the runner writing those jobs was
 *  deleted. */
export interface OnboardingCandidate {
  slug: string;
  display_name: string | null;
  /** What decides the order: the queue is taken smallest-first, so a 3-member community
   *  is never stuck behind a 45,772-member one. */
  total_members: number | null;
  source: 'registration' | 'discovered';
  attempts: number;
  queued_at: string | null;
  last_attempt_at: string | null;
  /** null when parked — the retry schedule has given up and a person should look. */
  next_attempt_at: string | null;
  parked: boolean;
  /** Being onboarded right now. One at a time, fleet-wide. */
  running: boolean;
  /** True when a PERSON blocked it, rather than the schedule giving up. Two
   *  different facts: "we decided" vs "it kept failing". */
  blocked: boolean;
  block_reason: string | null;
  last_error: string | null;
}

/** A queue-driven step plus its backlog depth. `waiting` is the point: these steps
 *  fetch whatever is still missing, and they run per community now, so when one last
 *  ran says nothing about whether the fleet is keeping up. */
export interface CorpusStage {
  name: string;
  cell: StageCell | null;
  waiting: number;
}

export interface PipelinePage extends ListEnvelope<PipelineCommunity> {
  global_steps: Record<string, StageCell>;
  stages: StageMeta[];
  jobs: JobRecord[];
  /** FLEET-WIDE, not per page: both counts drive a banner and a toggle that must not
   *  change as the grid is paged. */
  failed_count: number;
  lost_access_count: number;
  onboarding: OnboardingCandidate[];
  corpus: CorpusStage[];
  /** The fetch plane's own block, which has no community row to live in. */
  fetch_plane: FetchPlane;
}

/** The STAFF-wide pipeline grid — every account's jobs, not one community's CRM
 *  board (`./pipelines`). Two resources, one former name; the health page is this. */
export function fetchPipelineHealth(
  opts: { limit?: number; cursor?: string | null } = {},
): Promise<PipelinePage> {
  return apiGet<PipelinePage>('/pipelines', {
    limit: opts.limit,
    cursor: opts.cursor ?? undefined,
  });
}

/** The whole grid, walked — the sibling blocks come from the first page and survive
 *  the walk (see `collect`). */
export function fetchAllPipelines(): Promise<PipelinePage> {
  return collect((cursor) => fetchPipelineHealth({ limit: MAX_LIMIT, cursor }));
}

/** A community that has run the public keyword audit. */
export interface AuditLead {
  slug: string;
  display_name: string;
  url: string;
  /** How many times it has been audited — a repeat run is the outreach signal. */
  runs: number;
  first_seen: string;
  last_seen: string;
  email?: string | null;
  total_members: number;
  price_amount?: number | null;
  price_currency: string;
  price_interval: string;
  /** Our Skool account there, from our memberships list. `listed` is a membership OR a
   *  pending request (Skool lists both) — ask `fetchAuditLeadMembership` which. Null
   *  when the list could not be read. */
  membership?: 'joined' | 'listed' | 'none' | null;
  /** When the "DM owner" button messaged this lead's owner; null if never. */
  dm_sent_at?: string | null;
}

export interface AuditLeadPage {
  items: AuditLead[];
  total: number;
}

/** Who has run the public keyword audit, grouped by community, most recent first. */
export function fetchAuditLeads(limit = 500): Promise<AuditLeadPage> {
  return apiGet<AuditLeadPage>('/audit-leads', { limit });
}

/** Our live role in one lead's community: `member`, `pending`, `invited`, a staff role,
 *  or null when it could not be read. One browser fetch server-side — ask one at a time. */
/** The "DM owner" counter: DMs sent in the last 24 hours, and the cap. */
export interface OutreachQuota {
  used: number;
  limit: number;
}

export function fetchOutreachQuota(): Promise<OutreachQuota> {
  return apiGet<OutreachQuota>('/audit-leads/outreach');
}

/** DM a lead community's owner the outreach template. Refusals (daily limit, already
 *  sent, not joined, owner unknown, no template) arrive as errors naming why; a send
 *  Skool rejected comes back with `status: 'failed'`. */
export function sendLeadDm(slug: string): Promise<
  OutreachQuota & { slug: string; status: 'sent' | 'failed'; sent_at: string; error?: string | null }
> {
  return apiPost(`/audit-leads/${encodeURIComponent(slug)}/dm`);
}

export function fetchAuditLeadMembership(
  slug: string,
): Promise<{ slug: string; role: string | null }> {
  return apiGet<{ slug: string; role: string | null }>(
    `/audit-leads/${encodeURIComponent(slug)}/membership`,
  );
}
